import { TRACKING_STATUS_COLLECT, TRACKING_STATUS_LOCKER, TRACKING_STATUS_ORDER } from '@/constants'
import { StatusItem, StatusItemVue } from '@/types'
import { AVAILABLE_LEVEL_SERVICES } from '@meltingpoint/shared-objects'
import {
  COLLECT_STATUSES,
  DELIVERY_STATUSES,
  PICKUP_POINT_BOOKING_STATUSES,
} from '@meltingpoint/shared-statuses'

export class TimelineTrackingService {
  getStatusCollect(status: StatusItem[]) {
    const standardStatus: StatusItemVue[] = [
      { label: 'drive.tracking.validate', status: TRACKING_STATUS_COLLECT.COLLECT_VALIDATED },
      { label: 'drive.tracking.ready', status: TRACKING_STATUS_COLLECT.COLLECT_READY },
      { label: 'drive.tracking.ok', status: TRACKING_STATUS_COLLECT.COLLECT_DELIVERED },
    ]

    let localError = null
    const localSelectedStatus: StatusItemVue[] = []

    for (const statusItem of status) {
      switch (statusItem.status) {
        case COLLECT_STATUSES.PICK_UP_OK:
          const existingIndexDelivered = localSelectedStatus.findIndex(
            ({ status: currentStatus }) =>
              currentStatus === TRACKING_STATUS_COLLECT.COLLECT_DELIVERED,
          )

          if (existingIndexDelivered === -1) {
            localSelectedStatus.push({
              ...statusItem,
              status: TRACKING_STATUS_COLLECT.COLLECT_DELIVERED,
              label: 'drive.tracking.ok',
            })
          } else {
            localSelectedStatus[existingIndexDelivered] = {
              ...statusItem,
              status: TRACKING_STATUS_COLLECT.COLLECT_DELIVERED,
              label: 'drive.tracking.ready',
            }
          }
          break
        case COLLECT_STATUSES.READY:
        case COLLECT_STATUSES.AWAITING:
        case COLLECT_STATUSES.IN_PROGRESS:
        case COLLECT_STATUSES.DELAYED:
          const existingIndexReady = localSelectedStatus.findIndex(
            ({ status: currentStatus }) => currentStatus === TRACKING_STATUS_COLLECT.COLLECT_READY,
          )
          if (existingIndexReady === -1) {
            localSelectedStatus.push({
              ...statusItem,
              status: TRACKING_STATUS_COLLECT.COLLECT_READY,
              label: 'drive.tracking.ready',
            })
          } else {
            localSelectedStatus[existingIndexReady] = {
              ...statusItem,
              status: TRACKING_STATUS_COLLECT.COLLECT_READY,
              label: 'drive.tracking.ready',
            }
          }
          break
        case COLLECT_STATUSES.INIT:
        case COLLECT_STATUSES.STOCK_OK:
        case COLLECT_STATUSES.STARTED:
          const existingIndexValidated = localSelectedStatus.findIndex(
            ({ status: currentStatus }) =>
              currentStatus === TRACKING_STATUS_COLLECT.COLLECT_VALIDATED,
          )
          if (existingIndexValidated === -1) {
            localSelectedStatus.push({
              ...statusItem,
              status: TRACKING_STATUS_COLLECT.COLLECT_VALIDATED,
              label: 'drive.tracking.validate',
            })
          } else {
            localSelectedStatus[existingIndexValidated] = {
              ...statusItem,
              status: TRACKING_STATUS_COLLECT.COLLECT_VALIDATED,
              label: 'drive.tracking.validate',
            }
          }
          break
        case COLLECT_STATUSES.PICK_UP_FAILED:
        case COLLECT_STATUSES.BLOCKED:
          localSelectedStatus.push({
            ...statusItem,
            status: TRACKING_STATUS_COLLECT.COLLECT_BLOCKED_OR_FAILED,
            label: 'drive.tracking.blockedOrFailed',
          })
          localError = true
          break
        case COLLECT_STATUSES.CANCELLED:
          localSelectedStatus.push({
            ...statusItem,
            status: TRACKING_STATUS_COLLECT.COLLECT_CANCELLED,
            label: 'drive.tracking.cancelled',
          })
          localError = false
          break
      }
    }

    if (!localError) {
      for (const currentStandardStatus of standardStatus) {
        if (
          !localSelectedStatus.find(
            ({ status: currentStatus }) => currentStatus === currentStandardStatus.status,
          )
        ) {
          localSelectedStatus.push(currentStandardStatus)
        }
      }
    }

    const lastIndexWithDate = standardStatus.findIndex(
      standardItem =>
        standardItem.status ===
        [...localSelectedStatus].reverse().find(localStatusItem => localStatusItem.date)?.status,
    )

    return localSelectedStatus.filter((localStatusItem: StatusItemVue | null) => {
      if (!localStatusItem) {
        return false
      }

      if (
        standardStatus.findIndex(standardItem => standardItem.status === localStatusItem.status) <=
        lastIndexWithDate
      ) {
        return Boolean(localStatusItem.date)
      }

      return true
    })
  }

  getStatus(status: StatusItem[], services?: string[]) {
    const normalizedStatus = status.map(item => ({
      ...item,
      status: item.status === 'DELIVERY_DELIVERED_OK' ? DELIVERY_STATUSES.OK : item.status,
    }))

    const isPickupPoint = services?.includes(AVAILABLE_LEVEL_SERVICES.PICKUP_POINT)
    const isReturn = services?.includes(AVAILABLE_LEVEL_SERVICES.RETURN)

    const standardStatus: StatusItemVue[] = [
      {
        label: isReturn ? 'tracking.return.validatedOrder' : 'tracking.validatedOrder',
        status: TRACKING_STATUS_ORDER.ORDER_VALIDATED,
        deliveryStatus: DELIVERY_STATUSES.INIT,
        isRequired: true,
      },
      {
        label: 'tracking.appointmentScheduling',
        status: TRACKING_STATUS_ORDER.APPOINTMENT_SCHEDULING,
        deliveryStatus: DELIVERY_STATUSES.TEAM_ASSIGNED,
      },
      {
        label: 'tracking.carrierSupportKo',
        status: TRACKING_STATUS_ORDER.CARRIER_SUPPORT_KO,
        deliveryStatus: DELIVERY_STATUSES.PICK_UP_KO,
      },
      {
        label: 'tracking.carrierSupport',
        status: TRACKING_STATUS_ORDER.CARRIER_SUPPORT,
        deliveryStatus: DELIVERY_STATUSES.PICK_UP_OK,
        isRequired: true,
        conflictWith: DELIVERY_STATUSES.PICK_UP_KO,
      },
      {
        label: 'tracking.deliveryInTransit',
        status: TRACKING_STATUS_ORDER.DELIVERY_IN_TRANSIT,
        deliveryStatus: DELIVERY_STATUSES.IN_TRANSIT,
      },
      {
        label: isReturn ? 'tracking.return.deliveryOnGoing' : 'tracking.deliveryOnGoing',
        status: TRACKING_STATUS_ORDER.DELIVERY_ON_GOING,
        deliveryStatus: DELIVERY_STATUSES.IN_PROGRESS,
        isRequired: true,
      },
      {
        label: 'tracking.deliveryAvailable',
        status: TRACKING_STATUS_ORDER.DELIVERY_AVAILABLE,
        deliveryStatus: DELIVERY_STATUSES.AVAILABLE,
        isRequired: isPickupPoint,
      },
      {
        label: 'tracking.deliveryAtDropOffLocation',
        status: TRACKING_STATUS_ORDER.DELIVERY_AT_DROP_OFF_LOCATION,
        deliveryStatus: DELIVERY_STATUSES.AT_DROP_OFF_LOCATION,
      },
      {
        label: 'tracking.deliveryReturnedToSender',
        status: TRACKING_STATUS_ORDER.RETURNED_TO_SENDER,
        deliveryStatus: DELIVERY_STATUSES.RETURNED_TO_SENDER,
      },
      {
        label: 'tracking.cancelled',
        status: TRACKING_STATUS_ORDER.ORDER_DELIVERY_CANCELED,
        deliveryStatus: DELIVERY_STATUSES.CANCELLED,
      },
      {
        label: 'tracking.deliveryKo',
        status: TRACKING_STATUS_ORDER.ORDER_KO,
        deliveryStatus: DELIVERY_STATUSES.KO,
      },
      {
        label: isReturn ? 'tracking.return.deliveryOk' : 'tracking.deliveryOk',
        status: TRACKING_STATUS_ORDER.ORDER_DELIVERED,
        deliveryStatus: DELIVERY_STATUSES.OK,
        isRequired: true,
        conflictWith: DELIVERY_STATUSES.RETURNED_TO_SENDER && DELIVERY_STATUSES.KO,
      },
    ]

    normalizedStatus.sort(
      (statusA, statusB) =>
        new Date((statusB.date as unknown) as string).getTime() -
        new Date((statusA.date as unknown) as string).getTime(),
    )
    for (const statusItem of normalizedStatus.reverse()) {
      switch (statusItem.status) {
        case DELIVERY_STATUSES.TEAM_ASSIGNED:
        case DELIVERY_STATUSES.REPLANNED:
          const existingIndexTeamAssigned = standardStatus.findIndex(
            ({ status: currentStatus }) =>
              currentStatus === TRACKING_STATUS_ORDER.APPOINTMENT_SCHEDULING,
          )
          if (existingIndexTeamAssigned !== -1) {
            standardStatus[existingIndexTeamAssigned] = {
              ...standardStatus[existingIndexTeamAssigned],
              ...statusItem,
              status: TRACKING_STATUS_ORDER.APPOINTMENT_SCHEDULING,
            }
          }
          break
        case DELIVERY_STATUSES.IN_PROGRESS:
        case DELIVERY_STATUSES.CUSTOMS_BLOCKED:
        case DELIVERY_STATUSES.CUSTOMS_DELAYED:
        case DELIVERY_STATUSES.CUSTOMS_CLEARANCE_COMPLETED:
        case DELIVERY_STATUSES.CUSTOMS_CLEARANCE_IN_PROGRESS:
        case DELIVERY_STATUSES.TRANSIT_IN_DESTINATION_COUNTRY:
          const existingIndexInProgress = standardStatus.findIndex(
            ({ status: currentStatus }) =>
              currentStatus === TRACKING_STATUS_ORDER.DELIVERY_ON_GOING,
          )
          if (existingIndexInProgress !== -1) {
            standardStatus[existingIndexInProgress] = {
              ...standardStatus[existingIndexInProgress],
              ...statusItem,
              status: TRACKING_STATUS_ORDER.DELIVERY_ON_GOING,
            }
          }
          break
        case DELIVERY_STATUSES.KO:
          const indexKoBefore = standardStatus.findIndex(
            ({ deliveryStatus }) => deliveryStatus === DELIVERY_STATUSES.KO,
          )

          standardStatus[indexKoBefore] = {
            ...statusItem,
            deliveryStatus: DELIVERY_STATUSES.KO,
            label: 'tracking.deliveryKo',
            status: TRACKING_STATUS_ORDER.ORDER_KO,
          }
          break
        case DELIVERY_STATUSES.OK:
          const indexKo = standardStatus.findIndex(
            ({ deliveryStatus }) => deliveryStatus === DELIVERY_STATUSES.KO,
          )
          const isLastOK =
            normalizedStatus[normalizedStatus.length - 1].status === DELIVERY_STATUSES.OK
          if (indexKo !== -1 && isLastOK) {
            standardStatus.splice(indexKo, 1)
          }
          break
        case DELIVERY_STATUSES.AVAILABLE:
          const indexStatutKo = standardStatus.findIndex(
            ({ deliveryStatus }) => deliveryStatus === DELIVERY_STATUSES.KO,
          )

          const isLastAvailable =
            normalizedStatus[normalizedStatus.length - 1].status === DELIVERY_STATUSES.AVAILABLE
          if (indexStatutKo !== -1 && isLastAvailable) {
            standardStatus[indexStatutKo] = {
              ...statusItem,
              deliveryStatus: DELIVERY_STATUSES.OK,
              label: 'tracking.deliveryOk',
              status: TRACKING_STATUS_ORDER.ORDER_DELIVERED,
              date: undefined,
            }
          }

          const indexAvailable = standardStatus.findIndex(
            ({ deliveryStatus }) => deliveryStatus === DELIVERY_STATUSES.AVAILABLE,
          )

          if (indexAvailable !== -1) {
            standardStatus[indexAvailable] = {
              ...statusItem,
              deliveryStatus: DELIVERY_STATUSES.AVAILABLE,
              label: 'tracking.deliveryAvailable',
              status: TRACKING_STATUS_ORDER.DELIVERY_AVAILABLE,
            }
          }
          break
        case DELIVERY_STATUSES.PICK_UP_OK:
          const indexPickUpKo = standardStatus.findIndex(
            ({ deliveryStatus }) => deliveryStatus === DELIVERY_STATUSES.PICK_UP_KO,
          )
          if (indexPickUpKo !== -1) {
            standardStatus.splice(indexPickUpKo, 1)
          } else {
            standardStatus.push({
              ...statusItem,
              deliveryStatus: DELIVERY_STATUSES.PICK_UP_OK,
              label: 'tracking.carrierSupport',
              status: TRACKING_STATUS_ORDER.CARRIER_SUPPORT,
            })
          }
          break
      }
    }
    const filteredStatuses: StatusItemVue[] = standardStatus
      .map(value => {
        const statusFound = normalizedStatus.find(
          ({ status: deliveryStatus }) => deliveryStatus === value.deliveryStatus,
        )

        return {
          ...statusFound,
          ...value,
        }
      })
      .filter(
        (statusObject: StatusItemVue, index, statusArray) =>
          (!statusObject.conflictWith ||
            (statusObject.conflictWith &&
              !statusArray.find(
                ({ deliveryStatus, date }) => deliveryStatus === statusObject.conflictWith && date,
              ))) &&
          (statusObject.isRequired || statusObject.date),
      )
    const formatedStatuses = this.formatDeliveryTimeline(filteredStatuses)
    // reverse for populate previous status without date
    formatedStatuses.reverse()

    for (const [index, formatedStatus] of formatedStatuses.entries()) {
      if (!formatedStatus.date) {
        continue
      }

      if (formatedStatuses.length === index + 1) {
        continue
      }

      const nextStatus = formatedStatuses[index + 1]

      if (!nextStatus.date) {
        nextStatus.date = formatedStatus.date
      }
    }

    // reverse in the right order
    formatedStatuses.reverse()
    return formatedStatuses
  }

  getBookingStatus(status: StatusItem[], deliveryStatus?: StatusItem[]) {
    const concatStatus = deliveryStatus?.length ? [...status, ...deliveryStatus] : status

    const normalizedConcatStatus = concatStatus.map(item => ({
      ...item,
      status: item.status === 'DELIVERY_DELIVERED_OK' ? DELIVERY_STATUSES.OK : item.status,
    }))

    let standardStatus: StatusItemVue[] = []

    if (deliveryStatus?.length) {
      standardStatus = [
        { label: 'locker.tracking.validate', status: TRACKING_STATUS_LOCKER.BOOKING_VALIDATED },
        { label: 'tracking.carrierSupport', status: TRACKING_STATUS_ORDER.CARRIER_SUPPORT },
        { label: 'tracking.deliveryOnGoing', status: TRACKING_STATUS_ORDER.DELIVERY_ON_GOING },
        { label: 'locker.tracking.ready', status: TRACKING_STATUS_LOCKER.BOOKING_READY },
        { label: 'locker.tracking.ok', status: TRACKING_STATUS_LOCKER.BOOKING_DELIVERED },
      ]
    } else {
      standardStatus = [
        { label: 'locker.tracking.validate', status: TRACKING_STATUS_LOCKER.BOOKING_VALIDATED },
        { label: 'locker.tracking.ready', status: TRACKING_STATUS_LOCKER.BOOKING_READY },
        { label: 'locker.tracking.ok', status: TRACKING_STATUS_LOCKER.BOOKING_DELIVERED },
      ]
    }

    let localError = null
    const localSelectedStatus: StatusItemVue[] = []

    normalizedConcatStatus.sort(
      (statusA, statusB) => new Date(statusB.date!).getTime() - new Date(statusA.date!).getTime(),
    )

    for (const statusItem of normalizedConcatStatus.reverse()) {
      switch (statusItem.status) {
        case PICKUP_POINT_BOOKING_STATUSES.COLLECTION_OK:
          const existingIndexDelivered = localSelectedStatus.findIndex(
            ({ status: currentStatus }) =>
              currentStatus === TRACKING_STATUS_LOCKER.BOOKING_DELIVERED,
          )

          if (existingIndexDelivered === -1) {
            localSelectedStatus.push({
              ...statusItem,
              status: TRACKING_STATUS_LOCKER.BOOKING_DELIVERED,
              label: 'locker.tracking.ok',
            })
          } else {
            localSelectedStatus[existingIndexDelivered] = {
              ...statusItem,
              status: TRACKING_STATUS_LOCKER.BOOKING_DELIVERED,
              label: 'locker.tracking.ready',
            }
          }
          break

        case PICKUP_POINT_BOOKING_STATUSES.LOADING_OK:
        case DELIVERY_STATUSES.OK:
        case DELIVERY_STATUSES.AVAILABLE:
          const existingIndexReady = localSelectedStatus.findIndex(
            ({ status: currentStatus }) => currentStatus === TRACKING_STATUS_LOCKER.BOOKING_READY,
          )

          if (existingIndexReady === -1) {
            localSelectedStatus.push({
              ...statusItem,
              status: TRACKING_STATUS_LOCKER.BOOKING_READY,
              label: 'locker.tracking.ready',
            })
          } else {
            localSelectedStatus[existingIndexReady] = {
              ...statusItem,
              status: TRACKING_STATUS_LOCKER.BOOKING_READY,
              label: 'locker.tracking.ready',
            }
          }
          break

        case DELIVERY_STATUSES.IN_PROGRESS:
          const existingIndexInProgress = localSelectedStatus.findIndex(
            ({ status: currentStatus }) =>
              currentStatus === TRACKING_STATUS_ORDER.DELIVERY_ON_GOING,
          )

          if (existingIndexInProgress === -1) {
            localSelectedStatus.push({
              ...statusItem,
              status: TRACKING_STATUS_ORDER.DELIVERY_ON_GOING,
              label: 'tracking.deliveryOnGoing',
            })
          } else {
            localSelectedStatus[existingIndexInProgress] = {
              ...statusItem,
              status: TRACKING_STATUS_ORDER.DELIVERY_ON_GOING,
              label: 'tracking.deliveryOnGoing',
            }
          }
          break

        case DELIVERY_STATUSES.PICK_UP_OK:
          const existingIndexPicked = localSelectedStatus.findIndex(
            ({ status: currentStatus }) => currentStatus === TRACKING_STATUS_ORDER.CARRIER_SUPPORT,
          )

          if (existingIndexPicked === -1) {
            localSelectedStatus.push({
              ...statusItem,
              status: TRACKING_STATUS_ORDER.CARRIER_SUPPORT,
              label: 'tracking.carrierSupport',
            })
          } else {
            localSelectedStatus[existingIndexPicked] = {
              ...statusItem,
              status: TRACKING_STATUS_ORDER.CARRIER_SUPPORT,
              label: 'tracking.carrierSupport',
            }
          }
          break

        case PICKUP_POINT_BOOKING_STATUSES.BOOKING_INIT:
        case PICKUP_POINT_BOOKING_STATUSES.BOOKING_OK:
        case DELIVERY_STATUSES.INIT:
          const existingIndexValidated = localSelectedStatus.findIndex(
            ({ status: currentStatus }) =>
              currentStatus === TRACKING_STATUS_LOCKER.BOOKING_VALIDATED,
          )
          if (existingIndexValidated === -1) {
            localSelectedStatus.push({
              ...statusItem,
              status: TRACKING_STATUS_LOCKER.BOOKING_VALIDATED,
              label: 'locker.tracking.validate',
            })
          } else {
            localSelectedStatus[existingIndexValidated] = {
              ...statusItem,
              status: TRACKING_STATUS_LOCKER.BOOKING_VALIDATED,
              label: 'locker.tracking.validate',
            }
          }
          break

        case PICKUP_POINT_BOOKING_STATUSES.LOADING_KO:
        case PICKUP_POINT_BOOKING_STATUSES.BOOKING_KO:
        case PICKUP_POINT_BOOKING_STATUSES.COLLECTION_KO:
          localSelectedStatus.push({
            ...statusItem,
            status: TRACKING_STATUS_LOCKER.BOOKING_KO,
            label: 'locker.tracking.loadingOrCollectionFailed',
          })
          localError = true
          break
        case DELIVERY_STATUSES.CANCELLED:
          localSelectedStatus.push({
            ...statusItem,
            status: TRACKING_STATUS_ORDER.ORDER_DELIVERY_CANCELED,
            label: 'tracking.cancelled',
          })
          localError = true
          break
        case PICKUP_POINT_BOOKING_STATUSES.BOOKING_CANCELLED:
        case DELIVERY_STATUSES.CANCELLED:
          localSelectedStatus.push({
            ...statusItem,
            status: TRACKING_STATUS_LOCKER.BOOKING_CANCELLED,
            label: 'locker.tracking.cancelled',
          })
          localError = true
          break
      }
    }

    if (!localError) {
      for (const currentStandardStatus of standardStatus) {
        if (
          !localSelectedStatus.find(
            ({ status: currentStatus }) => currentStatus === currentStandardStatus.status,
          )
        ) {
          localSelectedStatus.push(currentStandardStatus)
        }
      }
    }

    const lastIndexWithDate = standardStatus.findIndex(
      standardItem =>
        standardItem.status ===
        [...localSelectedStatus].reverse().find(localStatusItem => localStatusItem.date)?.status,
    )

    return localSelectedStatus.filter((localStatusItem: StatusItemVue | null) => {
      if (!localStatusItem) {
        return false
      }

      if (
        standardStatus.findIndex(standardItem => standardItem.status === localStatusItem.status) <=
        lastIndexWithDate
      ) {
        return Boolean(localStatusItem.date)
      }

      return true
    })
  }

  formatDeliveryTimeline(statuses: StatusItemVue[] = []): StatusItemVue[] {
    const canceledStatusIndex = statuses.findIndex(
      status => status.status === TRACKING_STATUS_ORDER.ORDER_DELIVERY_CANCELED,
    )
    const lastDateStatusIndex = statuses.findIndex(
      statusItem =>
        statusItem.status ===
        [...statuses]
          .reverse()
          .find(item => item.date && item.status !== TRACKING_STATUS_ORDER.ORDER_DELIVERY_CANCELED)
          ?.status,
    )
    if (canceledStatusIndex !== -1 && lastDateStatusIndex !== -1) {
      const canceledStatus = statuses[canceledStatusIndex]
      statuses.splice(canceledStatusIndex, 1)
      statuses.splice(lastDateStatusIndex + 1, 0, canceledStatus)
    }
    return statuses
  }
}

export default new TimelineTrackingService()
